import type { DimensionsDTO, PrintAreaPreviewAndEpsDTO } from '@/typings/preMerge/beforeMerge';

/**
 * The print area sector the merchant picked in the product builder. Only the identifying fields matter
 * here; the rest of the sector travels with the postMessage payload.
 */
export interface EditedPrintAreaSector {
    printAreaId?: string;
    placeholderId?: string;
    fileName?: string;
}

type PlaceholderCarrier = Pick<DimensionsDTO, 'placeholderId' | 'fileName'> | EditedPrintAreaSector;

const placeholderOf = (source?: PlaceholderCarrier | null): string => source?.placeholderId || source?.fileName || '';

/**
 * `printAreaSectors` is ordered by the merchant's print-area selection while `printAreaPreviewAndEps`
 * follows the template's EPS order. Resolve the edited print area by
 * identity, never by position.
 */
export const resolveEditedPrintArea = (
    printAreaPreviewAndEps: PrintAreaPreviewAndEpsDTO[] | undefined | null,
    printAreaSectors: EditedPrintAreaSector[] | undefined | null,
    sectorIndex: number
): PrintAreaPreviewAndEpsDTO | null => {
    const entries = printAreaPreviewAndEps || [];
    const sectors = printAreaSectors || [];
    const sector = sectors[sectorIndex];
    const positional = entries[sectorIndex] || entries[0] || null;
    if (!sector) return positional;

    // fallbackPrintAreaId carries the parent template product's uuid, which is what the sector holds when
    // the best match resolved to a child template product (printful/printify split their sizes that way).
    if (sector.printAreaId) {
        const byPrintAreaId = entries.find(
            (entry) =>
                entry.printAreaEps?.printAreaId === sector.printAreaId ||
                entry.printAreaEps?.fallbackPrintAreaId === sector.printAreaId
        );
        if (byPrintAreaId) return byPrintAreaId;
    }

    const sectorPlaceholder = placeholderOf(sector);
    const samePlaceholder = entries.filter((entry) => placeholderOf(entry.printAreaEps) === sectorPlaceholder);
    if (samePlaceholder.length === 1) return samePlaceholder[0];
    if (samePlaceholder.length > 1) {
        // Several print areas live in the same file: fall back to this sector's rank among its siblings,
        // which both sides order by the underlying image id.
        const siblingRank = sectors.slice(0, sectorIndex).filter((s) => placeholderOf(s) === sectorPlaceholder).length;
        return samePlaceholder[siblingRank] || samePlaceholder[0];
    }

    return positional;
};
